import { useState, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue,
  HStack,
  SimpleGrid,
  Progress,
  Tooltip,
  TableContainer,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  useToast,
  useDisclosure,
  Tag,
  TagLabel,
  TagCloseButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverCloseButton,
  Select,
  FormControl,
  FormLabel,
  VStack,
  Portal,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faEdit,
  faTrash,
  faPause,
  faPlay,
  faChartBar,
  faFilter,
  faCalendar,
  faImage,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useCampaigns, useCampaignMutations } from '../hooks/useCampaigns';
import { campaignsApi } from '../api/campaigns';
import { useZone } from '../hooks/useZones';
import { useAdvertiser } from '../hooks/useAdvertisers';
import { formatPercent } from '../utils/format';
import CampaignModal from '../components/campaigns/CampaignModal';
import CampaignStatsModal from '../components/campaigns/CampaignStatsModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import { useLicenseProtection } from '../hooks/useLicenseProtection';
import { formatFullNumber } from '../utils/format';
import DateRangePicker from '../components/common/DateRangePicker';
import { useRecoilState } from 'recoil';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { parseDateRange } from '../hooks/useAnalytics';

function getStatusColor(status) {
  switch (status) {
    case 'active':
      return 'green';
    case 'paused':
      return 'yellow';
    case 'completed':
      return 'gray';
    default:
      return 'gray';
  }
}

function CampaignCard({ campaign, onViewStats, onEdit, onPauseResume, onDelete, isReadOnly, onCampaignClick, onAdvertiserClick }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const progressBg = useColorModeValue('gray.100', 'gray.700');
  const spentPercentage = campaign.budget > 0 ? (campaign.spent / campaign.budget) * 100 : 0;

  return (
    <Card
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      _hover={{ transform: 'translateY(-1px)' }}
      transition="all 0.2s"
    >
      <CardBody>
        <Flex justify="space-between" align="start" mb={3}>
          <Box flex={1}>
            <Text
              fontWeight="600"
              noOfLines={1}
              color="brand.500"
              cursor="pointer"
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => {
                e.stopPropagation();
                onCampaignClick && onCampaignClick(campaign);
              }}
            >
              {campaign.name}
            </Text>
            <Text
              fontSize="sm"
              color="purple.500"
              cursor="pointer"
              _hover={{ textDecoration: 'underline' }}
              onClick={(e) => {
                e.stopPropagation();
                onAdvertiserClick && onAdvertiserClick(campaign);
              }}
            >
              {campaign.advertiser}
            </Text>
          </Box>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FontAwesomeIcon icon={faEllipsisV} />}
              variant="ghost"
              size="sm"
              borderRadius="lg"
            />
            <Portal>
              <MenuList zIndex={9999} py={2} borderRadius="xl">
                <MenuItem icon={<FontAwesomeIcon icon={faChartBar} />} onClick={() => onViewStats(campaign)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                  View Stats
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faEdit} />}
                  onClick={() => onEdit(campaign)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Edit
                </MenuItem>
                {campaign.status === 'active' ? (
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faPause} />}
                    onClick={() => onPauseResume(campaign)}
                    isDisabled={isReadOnly}
                    mx={2}
                    borderRadius="lg"
                    w="calc(100% - 16px)"
                  >
                    Pause
                  </MenuItem>
                ) : campaign.status === 'paused' ? (
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faPlay} />}
                    onClick={() => onPauseResume(campaign)}
                    isDisabled={isReadOnly}
                    mx={2}
                    borderRadius="lg"
                    w="calc(100% - 16px)"
                  >
                    Resume
                  </MenuItem>
                ) : null}
                <MenuItem
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  color={isReadOnly ? 'gray.400' : 'red.500'}
                  onClick={() => onDelete(campaign)}
                  isDisabled={isReadOnly}
                  mx={2}
                  borderRadius="lg"
                  w="calc(100% - 16px)"
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        <HStack spacing={2} mb={4}>
          <Badge colorScheme={getStatusColor(campaign.status)} borderRadius="full">
            {campaign.status}
          </Badge>
          <Badge variant="outline" borderRadius="full" fontSize="xs">
            {formatPercent(campaign.ctr, 2)} CTR
          </Badge>
        </HStack>

        <SimpleGrid columns={2} spacing={3} mb={4}>
          <Box>
            <Text fontSize="xs" color={textSecondary} textTransform="uppercase">Impressions</Text>
            <Text fontWeight="600">{formatFullNumber(campaign.impressions)}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={textSecondary} textTransform="uppercase">Clicks</Text>
            <Text fontWeight="600">{formatFullNumber(campaign.clicks)}</Text>
          </Box>
        </SimpleGrid>

        <Box mb={3}>
          {campaign.billing_type === 'duration' ? (
            // Duration-based campaign
            (() => {
              const start = campaign.start_date ? new Date(campaign.start_date) : null;
              const end = campaign.end_date ? new Date(campaign.end_date) : null;
              if (!start || !end) return null;
              const totalDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
              const today = new Date();
              const elapsedDays = Math.max(0, Math.min(totalDays, Math.ceil((today - start) / (1000 * 60 * 60 * 24))));
              const progress = (elapsedDays / totalDays) * 100;
              return (
                <>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="xs" color={textSecondary}>Duration</Text>
                    <Text fontSize="xs" fontWeight="600">{elapsedDays} / {totalDays} days</Text>
                  </Flex>
                  <Progress
                    value={progress}
                    size="sm"
                    colorScheme="cyan"
                    borderRadius="full"
                    bg={progressBg}
                  />
                </>
              );
            })()
          ) : (
            // Monetary budget campaign
            <>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" color={textSecondary}>Budget Spent</Text>
                <Text fontSize="xs" fontWeight="600">${formatFullNumber(campaign.spent)} / ${formatFullNumber(campaign.budget)}</Text>
              </Flex>
              <Progress
                value={spentPercentage}
                size="sm"
                colorScheme={spentPercentage > 90 ? 'red' : spentPercentage > 70 ? 'yellow' : 'brand'}
                borderRadius="full"
                bg={progressBg}
              />
            </>
          )}
        </Box>

        <HStack spacing={1} fontSize="xs" color={textSecondary}>
          <FontAwesomeIcon icon={faCalendar} />
          <Text>{campaign.startDate} - {campaign.endDate}</Text>
        </HStack>
      </CardBody>
    </Card>
  );
}

function CampaignsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const advertiserFilter = searchParams.get('advertiser');
  const campaignFilter = searchParams.get('campaign');
  const zoneFilter = searchParams.get('zone');

  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const toast = useToast();
  const { isReadOnly, protectAction } = useLicenseProtection();

  // Modal states
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Local filter state
  const [filters, setFilters] = useState({
    advertiser: '',
    status: '',
  });

  // Date range state (shared globally across all tabs)
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);

  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
  }, [setDateRange]);

  const inputBg = useColorModeValue('white', 'gray.700');

  // Build API filters from URL params and date range
  const apiFilters = useMemo(() => {
    const dates = parseDateRange(dateRange);
    const params = { start: dates.start.toISOString(), end: dates.end.toISOString() };
    if (zoneFilter) params.zone = zoneFilter;
    return params;
  }, [zoneFilter, dateRange]);

  // Fetch campaigns from API
  const { data: campaigns, loading, error, refetch } = useCampaigns(apiFilters);

  // Fetch data for filter display
  const { data: zoneData } = useZone(zoneFilter);
  const { data: advertiserData } = useAdvertiser(advertiserFilter);

  // Extract unique filter options from campaigns
  const filterOptions = useMemo(() => {
    if (!campaigns) return { advertisers: [], statuses: [] };
    const advertisers = [...new Map(
      campaigns.map(c => [c.advertiser_id, { id: c.advertiser_id, name: c.advertiser_name }])
    ).values()].sort((a, b) => a.name?.localeCompare(b.name));
    const statuses = [...new Set(campaigns.map(c => c.status).filter(Boolean))];
    return { advertisers, statuses };
  }, [campaigns]);

  // Filter campaigns based on URL params and local filters (client-side filters)
  const filteredCampaigns = useMemo(() => {
    if (!campaigns) return null;
    let result = campaigns;

    // URL-based filters
    if (advertiserFilter) {
      result = result.filter(c => String(c.advertiser_id) === advertiserFilter);
    }
    if (campaignFilter) {
      result = result.filter(c => String(c.campaign_id) === campaignFilter);
    }

    // Local dropdown filters (only apply if no URL filter for same field)
    if (!advertiserFilter && filters.advertiser) {
      result = result.filter(c => String(c.advertiser_id) === filters.advertiser);
    }
    if (filters.status) {
      result = result.filter(c => c.status === filters.status);
    }

    return result;
  }, [campaigns, advertiserFilter, campaignFilter, filters]);

  // Get campaign name for filter display
  const filterCampaignName = useMemo(() => {
    if (!campaignFilter || !campaigns) return null;
    const campaign = campaigns.find(c => String(c.campaign_id) === campaignFilter);
    return campaign?.campaign_name;
  }, [campaigns, campaignFilter]);

  // Count active local filters
  const activeFilterCount = Object.values(filters).filter(v => v).length;

  // Clear a single local filter
  const clearLocalFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
  };

  // Clear all local filters
  const clearAllFilters = () => {
    setFilters({ advertiser: '', status: '' });
  };

  const clearFilter = (key) => {
    const newParams = new URLSearchParams(searchParams);
    if (key) {
      newParams.delete(key);
    } else {
      // Clear all
      return setSearchParams({});
    }
    setSearchParams(newParams);
  };
  const { createCampaign, updateCampaign, deleteCampaign } = useCampaignMutations();

  // Helper to format campaign data from API
  const formatCampaign = (c) => ({
    id: c.campaign_id || c.id,
    campaign_id: c.campaign_id || c.id,
    campaign_name: c.campaign_name || c.name,
    name: c.campaign_name || c.name,
    advertiser: c.advertiser_name || c.advertiser || 'Unknown',
    advertiser_id: c.advertiser_id,
    advertiser_name: c.advertiser_name,
    status: c.status || 'active',
    impressions: c.total_impressions || c.impressions || 0,
    clicks: c.total_clicks || c.clicks || 0,
    ctr: c.ctr || (c.total_impressions > 0 ? ((c.total_clicks || 0) / c.total_impressions * 100) : 0),
    budget: c.budget || 0,
    spent: c.spent || c.total_spent || 0,
    billing_type: c.billing_type || 'duration',
    ad_count: c.ad_count || 0,
    startDate: c.start_date ? new Date(c.start_date).toLocaleDateString() : '-',
    endDate: c.end_date ? new Date(c.end_date).toLocaleDateString() : '-',
    start_date: c.start_date,
    end_date: c.end_date,
    description: c.description,
    total_impressions_limit: c.total_impressions_limit,
    daily_impressions_limit: c.daily_impressions_limit,
    priority: c.priority,
    weight: c.weight,
    created_at: c.created_at,
  });

  const campaignsList = filteredCampaigns ? filteredCampaigns.map(formatCampaign) : [];

  // Handlers
  const handleViewStats = (campaign) => {
    setSelectedCampaign(campaign);
    onStatsOpen();
  };

  const handleEdit = async (campaign) => {
    try {
      // Fetch full campaign data including zones
      const result = await campaignsApi.getById(campaign.id);
      const fullCampaign = result.data || result;
      setSelectedCampaign({
        ...campaign,
        ...fullCampaign,
        zones: fullCampaign.zones || [],
      });
      onEditOpen();
    } catch (err) {
      toast({
        title: 'Error loading campaign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handlePauseResume = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      await updateCampaign(campaign.id, { status: newStatus });
      toast({
        title: `Campaign ${newStatus === 'active' ? 'resumed' : 'paused'}`,
        status: 'success',
        duration: 3000,
      });
      refetch();
    } catch (err) {
      toast({
        title: 'Error updating campaign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDeleteClick = (campaign) => {
    setSelectedCampaign(campaign);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCampaign) return;
    setIsSubmitting(true);
    try {
      await deleteCampaign(selectedCampaign.id);
      toast({
        title: 'Campaign deleted',
        status: 'success',
        duration: 3000,
      });
      onDeleteClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error deleting campaign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await createCampaign(data);
      toast({
        title: 'Campaign created',
        status: 'success',
        duration: 3000,
      });
      onCreateClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error creating campaign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (data) => {
    if (!selectedCampaign) return;
    setIsSubmitting(true);
    try {
      await updateCampaign(selectedCampaign.id, data);
      toast({
        title: 'Campaign updated',
        status: 'success',
        duration: 3000,
      });
      onEditClose();
      refetch();
    } catch (err) {
      toast({
        title: 'Error updating campaign',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, accent.500)"
            bgClip="text"
            fontWeight="700"
          >
            Campaigns
          </Heading>
          <Text color={textSecondary} mt={1}>
            Manage your advertising campaigns
          </Text>
        </Box>
        <HStack spacing={3} flexWrap="wrap">
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={false}
            size="md"
          />
          <Popover
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            placement="bottom-end"
          >
            <PopoverTrigger>
              <Button
                leftIcon={<FontAwesomeIcon icon={faFilter} />}
                variant="outline"
                size={{ base: 'sm', md: 'md' }}
                borderRadius="xl"
                onClick={onFilterOpen}
              >
                Filter
                {activeFilterCount > 0 && (
                  <Badge
                    colorScheme="brand"
                    borderRadius="full"
                    ml={2}
                    fontSize="xs"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              bg={cardBg}
              borderColor={borderColor}
              borderRadius="xl"
              w="280px"
              boxShadow="lg"
            >
              <PopoverHeader fontWeight="600" borderColor={borderColor}>
                Filter Campaigns
              </PopoverHeader>
              <PopoverCloseButton />
              <PopoverBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm">Advertiser</FormLabel>
                    <Select
                      placeholder="All advertisers"
                      value={filters.advertiser}
                      onChange={(e) => setFilters(prev => ({ ...prev, advertiser: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.advertisers.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Status</FormLabel>
                    <Select
                      placeholder="All statuses"
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      bg={inputBg}
                      borderRadius="lg"
                      size="sm"
                    >
                      {filterOptions.statuses.map(s => (
                        <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                      ))}
                    </Select>
                  </FormControl>
                </VStack>
              </PopoverBody>
              {activeFilterCount > 0 && (
                <PopoverFooter borderColor={borderColor}>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={clearAllFilters}
                    leftIcon={<FontAwesomeIcon icon={faTimes} />}
                  >
                    Clear all filters
                  </Button>
                </PopoverFooter>
              )}
            </PopoverContent>
          </Popover>
          <Button
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            colorScheme="brand"
            size={{ base: 'sm', md: 'md' }}
            borderRadius="xl"
            onClick={() => protectAction(onCreateOpen)}
            isDisabled={isReadOnly}
            title={isReadOnly ? 'License required to create campaigns' : undefined}
          >
            New Campaign
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {(() => {
        const totals = campaignsList.reduce(
          (acc, c) => ({
            impressions: acc.impressions + (c.impressions || 0),
            clicks: acc.clicks + (c.clicks || 0),
            ads: acc.ads + (c.ad_count || 0),
          }),
          { impressions: 0, clicks: 0, ads: 0 }
        );
        const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
        return (
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, blue.400, blue.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Campaigns</Text>
                  <Text fontSize="2xl" fontWeight="bold">{campaignsList.length}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, purple.400, purple.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Impressions</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.impressions)}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, green.400, green.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Total Clicks</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatFullNumber(totals.clicks)}</Text>
                </VStack>
              </CardBody>
            </Card>
            <Card bg={cardBg} border="1px solid" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient="linear(to-r, orange.400, orange.600)" />
              <CardBody py={4}>
                <VStack align="start" spacing={1}>
                  <Text color={textSecondary} fontSize="sm">Avg CTR</Text>
                  <Text fontSize="2xl" fontWeight="bold">{formatPercent(avgCtr, 2, true)}</Text>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        );
      })()}

      {/* Active Filter Display */}
      {(advertiserFilter || campaignFilter || zoneFilter || activeFilterCount > 0) && (
        <Flex mb={4} gap={2} flexWrap="wrap">
          {/* URL-based filters */}
          {advertiserFilter && (
            <Tag size="lg" borderRadius="full" colorScheme="purple" variant="subtle">
              <TagLabel>Advertiser: {advertiserData?.advertiser_name || `#${advertiserFilter}`}</TagLabel>
              <TagCloseButton onClick={() => clearFilter('advertiser')} />
            </Tag>
          )}
          {campaignFilter && filterCampaignName && (
            <Tag size="lg" borderRadius="full" colorScheme="blue" variant="subtle">
              <TagLabel>Campaign: {filterCampaignName}</TagLabel>
              <TagCloseButton onClick={() => clearFilter('campaign')} />
            </Tag>
          )}
          {zoneFilter && (
            <Tag size="lg" borderRadius="full" colorScheme="green" variant="subtle">
              <TagLabel>Zone: {zoneData?.zone_name || `#${zoneFilter}`}</TagLabel>
              <TagCloseButton onClick={() => clearFilter('zone')} />
            </Tag>
          )}
          {/* Local dropdown filters */}
          {!advertiserFilter && filters.advertiser && (
            <Tag size="md" borderRadius="full" colorScheme="purple" variant="subtle">
              <TagLabel>Advertiser: {filterOptions.advertisers.find(a => String(a.id) === filters.advertiser)?.name}</TagLabel>
              <TagCloseButton onClick={() => clearLocalFilter('advertiser')} />
            </Tag>
          )}
          {filters.status && (
            <Tag size="md" borderRadius="full" colorScheme="green" variant="subtle">
              <TagLabel>Status: {filters.status}</TagLabel>
              <TagCloseButton onClick={() => clearLocalFilter('status')} />
            </Tag>
          )}
        </Flex>
      )}

      {/* Error State */}
      {error && (
        <Alert status="error" borderRadius="xl" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <>
          {/* Mobile Loading */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            spacing={4}
            display={{ base: 'grid', lg: 'none' }}
          >
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardBody>
                  <Skeleton height="20px" mb={2} />
                  <Skeleton height="14px" width="60%" mb={4} />
                  <SkeletonText noOfLines={4} spacing={2} />
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          {/* Desktop Loading */}
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} display={{ base: 'none', lg: 'block' }}>
            <CardBody>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height="50px" mb={3} borderRadius="md" />
              ))}
            </CardBody>
          </Card>
        </>
      ) : campaignsList.length > 0 ? (
        <>
          {/* Mobile Card View */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            spacing={4}
            display={{ base: 'grid', lg: 'none' }}
          >
            {campaignsList.map((campaign) => (
              <CampaignCard
                key={campaign.id}
                campaign={campaign}
                onViewStats={handleViewStats}
                onEdit={(c) => protectAction(() => handleEdit(c))}
                onPauseResume={(c) => protectAction(() => handlePauseResume(c))}
                onDelete={(c) => protectAction(() => handleDeleteClick(c))}
                isReadOnly={isReadOnly}
                onCampaignClick={(c) => handleEdit(c)}
                onAdvertiserClick={(c) => navigate(`/campaigns?advertiser=${c.advertiser_id}`)}
              />
            ))}
          </SimpleGrid>

          {/* Desktop Table View */}
          <Card
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            display={{ base: 'none', lg: 'block' }}
          >
            <CardBody p={0}>
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Campaign</Th>
                      <Th>Status</Th>
                      <Th isNumeric>Ads</Th>
                      <Th isNumeric>Impressions</Th>
                      <Th isNumeric>Clicks</Th>
                      <Th isNumeric>CTR</Th>
                      <Th>Budget</Th>
                      <Th>Dates</Th>
                      <Th width="50px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {campaignsList.map((campaign) => (
                      <Tr key={campaign.id} _hover={{ bg: hoverBg }} transition="all 0.2s">
                        <Td>
                          <Box>
                            <Text
                              fontWeight="600"
                              color="brand.500"
                              cursor="pointer"
                              _hover={{ textDecoration: 'underline' }}
                              onClick={() => handleEdit(campaign)}
                            >
                              {campaign.name}
                            </Text>
                            <Text
                              fontSize="sm"
                              color="purple.500"
                              cursor="pointer"
                              _hover={{ textDecoration: 'underline' }}
                              onClick={() => navigate(`/campaigns?advertiser=${campaign.advertiser_id}`)}
                            >
                              {campaign.advertiser}
                            </Text>
                          </Box>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(campaign.status)} borderRadius="full">
                            {campaign.status}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Text
                            fontWeight="500"
                            color={campaign.ad_count > 0 ? 'purple.500' : undefined}
                            cursor={campaign.ad_count > 0 ? 'pointer' : 'default'}
                            _hover={campaign.ad_count > 0 ? { textDecoration: 'underline' } : {}}
                            onClick={() => campaign.ad_count > 0 && navigate(`/ads?campaign=${campaign.id}`)}
                          >
                            {campaign.ad_count}
                          </Text>
                        </Td>
                        <Td isNumeric fontWeight="500">{formatFullNumber(campaign.impressions)}</Td>
                        <Td isNumeric fontWeight="500">{formatFullNumber(campaign.clicks)}</Td>
                        <Td isNumeric>
                          <Badge variant="subtle" colorScheme="brand" borderRadius="full">
                            {formatPercent(campaign.ctr, 2)}
                          </Badge>
                        </Td>
                        <Td>
                          {campaign.billing_type === 'duration' ? (
                            // Duration-based campaign - show days progress
                            (() => {
                              const start = campaign.start_date ? new Date(campaign.start_date) : null;
                              const end = campaign.end_date ? new Date(campaign.end_date) : null;
                              if (!start || !end) return <Text fontSize="sm" color={textSecondary}>—</Text>;
                              const totalDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
                              const today = new Date();
                              const elapsedDays = Math.max(0, Math.min(totalDays, Math.ceil((today - start) / (1000 * 60 * 60 * 24))));
                              const progress = (elapsedDays / totalDays) * 100;
                              return (
                                <Tooltip label={`${Math.round(progress)}% elapsed`}>
                                  <Box>
                                    <Text fontSize="sm" fontWeight="500">
                                      {elapsedDays} / {totalDays} days
                                    </Text>
                                    <Progress
                                      value={progress}
                                      size="xs"
                                      colorScheme="cyan"
                                      borderRadius="full"
                                      mt={1}
                                    />
                                  </Box>
                                </Tooltip>
                              );
                            })()
                          ) : (
                            // Monetary budget campaign
                            <Tooltip label={campaign.budget > 0 ? `${Math.round((campaign.spent / campaign.budget) * 100)}% spent` : 'No budget set'}>
                              <Box>
                                <Text fontSize="sm" fontWeight="500">
                                  ${formatFullNumber(campaign.spent)} / ${formatFullNumber(campaign.budget)}
                                </Text>
                                <Progress
                                  value={campaign.budget > 0 ? (campaign.spent / campaign.budget) * 100 : 0}
                                  size="xs"
                                  colorScheme="brand"
                                  borderRadius="full"
                                  mt={1}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={textSecondary}>
                            {campaign.startDate}
                          </Text>
                          <Text fontSize="xs" color={textSecondary}>
                            to {campaign.endDate}
                          </Text>
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<FontAwesomeIcon icon={faEllipsisV} />}
                              variant="ghost"
                              size="sm"
                              borderRadius="lg"
                            />
                            <Portal>
                              <MenuList zIndex={9999} py={2} borderRadius="xl">
                                <MenuItem icon={<FontAwesomeIcon icon={faImage} />} onClick={() => navigate(`/ads?campaign=${campaign.id}`)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                                  View Ads
                                </MenuItem>
                                <MenuItem icon={<FontAwesomeIcon icon={faChartBar} />} onClick={() => handleViewStats(campaign)} mx={2} borderRadius="lg" w="calc(100% - 16px)">
                                  View Stats
                                </MenuItem>
                                <MenuItem
                                  icon={<FontAwesomeIcon icon={faEdit} />}
                                  onClick={() => protectAction(() => handleEdit(campaign))}
                                  isDisabled={isReadOnly}
                                  mx={2}
                                  borderRadius="lg"
                                  w="calc(100% - 16px)"
                                >
                                  Edit
                                </MenuItem>
                                {campaign.status === 'active' ? (
                                  <MenuItem
                                    icon={<FontAwesomeIcon icon={faPause} />}
                                    onClick={() => protectAction(() => handlePauseResume(campaign))}
                                    isDisabled={isReadOnly}
                                    mx={2}
                                    borderRadius="lg"
                                    w="calc(100% - 16px)"
                                  >
                                    Pause
                                  </MenuItem>
                                ) : campaign.status === 'paused' ? (
                                  <MenuItem
                                    icon={<FontAwesomeIcon icon={faPlay} />}
                                    onClick={() => protectAction(() => handlePauseResume(campaign))}
                                    isDisabled={isReadOnly}
                                    mx={2}
                                    borderRadius="lg"
                                    w="calc(100% - 16px)"
                                  >
                                    Resume
                                  </MenuItem>
                                ) : null}
                                <MenuItem
                                  icon={<FontAwesomeIcon icon={faTrash} />}
                                  color={isReadOnly ? 'gray.400' : 'red.500'}
                                  onClick={() => protectAction(() => handleDeleteClick(campaign))}
                                  isDisabled={isReadOnly}
                                  mx={2}
                                  borderRadius="lg"
                                  w="calc(100% - 16px)"
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </Portal>
                          </Menu>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </>
      ) : (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Text color={textSecondary} textAlign="center" py={8}>
              {campaignFilter
                ? 'Campaign not found.'
                : advertiserFilter
                  ? 'No campaigns found for this advertiser.'
                  : zoneFilter
                    ? 'No campaigns found for this zone.'
                    : 'No campaigns found. Create your first campaign to get started.'}
            </Text>
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      <CampaignModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onSubmit={handleCreateSubmit}
        isLoading={isSubmitting}
      />

      <CampaignModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSubmit={handleEditSubmit}
        campaign={selectedCampaign}
        isLoading={isSubmitting}
      />

      <CampaignStatsModal
        isOpen={isStatsOpen}
        onClose={onStatsClose}
        campaign={selectedCampaign}
      />

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Campaign"
        itemName={selectedCampaign?.name}
        isLoading={isSubmitting}
      />
    </Box>
  );
}

export default CampaignsPage;
